import { useCallback, useContext, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { useTranslation } from 'react-i18next'
import SectionTitle from '../../../SectionTitle'
import SocietyCard from './SocietyCard'
import SocietyFormDialog from './SocietyFormDialog'
import DeleteDialog from '../../../DeleteDialog'
import { authContext } from '../../../../contexts/AuthContext'
import {
  listSocietiesByClientId,
  insertSociety,
  updateSociety,
  deleteSociety,
  assignClientToSociety
} from '../../../../services/societies'
import { listSocietyCompliances } from '../../../../services/societyCompliances'
import { uploadSocietyLiteralDocument } from '../../../../services/societyDocuments'

// View of a client's legal entities ("sociedades") and their compliance
// status, backed by the same endpoints the (now hidden) admin Societies /
// Compliances pages used — see src/services/societies.js,
// src/services/societyCompliances.js and src/services/societyComplianceDocuments.js.
export default function ClientSocietiesSection({ clientId }) {
  const { t } = useTranslation()
  const { auth } = useContext(authContext)
  const canEdit = auth.type === 'blp'
  const [societies, setSocieties] = useState(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editingSociety, setEditingSociety] = useState(null)
  const [deleteCandidate, setDeleteCandidate] = useState({})
  const [refreshToken, setRefreshToken] = useState(0)

  const refresh = useCallback(async () => {
    try {
      const list = (await listSocietiesByClientId(clientId)) || []
      const withCompliances = await Promise.all(
        list.map(async (society) => ({
          ...society,
          compliances: (await listSocietyCompliances(society.id)) || []
        }))
      )
      setSocieties(withCompliances)
      setRefreshToken((prev) => prev + 1)
    } catch (err) {
      console.error('Error loading client societies:', err)
      setSocieties([])
    }
  }, [clientId])

  useEffect(() => {
    refresh()
  }, [refresh])

  const handleSaveSociety = async (value, literalFile) => {
    let societyId = value.id
    if (value.id) {
      await updateSociety({ ...value, updated_by: auth.user.id })
    } else {
      const created = await insertSociety({ ...value, loggedUserId: auth.user.id })
      societyId = created?.Id
      if (societyId) {
        await assignClientToSociety(clientId, societyId)
      }
    }

    if (literalFile && societyId) {
      try {
        await uploadSocietyLiteralDocument({
          file: literalFile,
          societyId,
          loggedUserId: auth.user.id
        })
      } catch (err) {
        console.error('Error uploading society literal document:', err)
      }
    }

    setFormOpen(false)
    setEditingSociety(null)
    await refresh()
  }

  const handleDeleteSociety = async (item) => {
    await deleteSociety({ id: item.id, updated_by: auth.user.id })
    await refresh()
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <SectionTitle>{t('societies_compliance_title')}</SectionTitle>
        {canEdit && (
          <Button
            variant='contained'
            startIcon={<AddRoundedIcon />}
            onClick={() => {
              setEditingSociety(null)
              setFormOpen(true)
            }}
            sx={{
              backgroundColor: window.$brand === 'PRB' ? '#17afb6' : '#102a46',
              borderRadius: window.$brand === 'PRB' ? 0 : 5
            }}
          >
            {t('add_legal_entity')}
          </Button>
        )}
      </Box>

      {!societies ? (
        <Stack spacing={2}>
          <Skeleton variant='rounded' height={160} />
          <Skeleton variant='rounded' height={160} />
        </Stack>
      ) : societies.length === 0 ? (
        <Typography color='text.secondary'>{t('no_legal_entities')}</Typography>
      ) : (
        <Stack spacing={3}>
          {societies.map((society) => (
            <SocietyCard
              key={society.id}
              society={society}
              clientId={clientId}
              canEdit={canEdit}
              refreshToken={refreshToken}
              onEdit={() => {
                setEditingSociety(society)
                setFormOpen(true)
              }}
              onDelete={() => setDeleteCandidate(society)}
              onChange={refresh}
            />
          ))}
        </Stack>
      )}

      <SocietyFormDialog
        open={formOpen}
        initialValue={editingSociety}
        onClose={() => {
          setFormOpen(false)
          setEditingSociety(null)
        }}
        onSave={handleSaveSociety}
      />

      <DeleteDialog
        item={deleteCandidate}
        title={t('delete_legal_entity')}
        caption={t('delete_legal_entity_caption')}
        targetFunc={handleDeleteSociety}
        handleDialogClose={() => setDeleteCandidate({})}
      />
    </Box>
  )
}
